import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { isDevOrLocal } from '../config/env';

export enum Background {
  Developer = 'Developer',
  CustomerExperience = 'CustomerExperience',
  Business = 'Business',
}

export interface Participant {
  background: Background | null;
  group: number | null;
}

interface ParticipantContextType {
  participant: Participant;
  setBackground: (background: Background) => void;
  setGroup: (group: number) => void;
}

const ParticipantContext = createContext<ParticipantContextType | undefined>(undefined);

export const ParticipantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();

  const [participant, setParticipant] = useState<Participant>(() => {
    let bgParam = null
    let groupParam = null

    if (isDevOrLocal) {
      const searchParams = new URLSearchParams(location.search);
      bgParam = searchParams.get('background');
      groupParam = searchParams.get('group');
    }

    let background: Background | null = null;
    if (bgParam && Object.values(Background).includes(bgParam as Background)) {
      background = bgParam as Background;
    }

    let group: number | null = null;
    if (groupParam && !isNaN(parseInt(groupParam))) {
      group = parseInt(groupParam);
    }

    return {
      background,
      group,
    };
  });

  const setBackground = React.useCallback((background: Background) => {
    setParticipant((prev) => ({ ...prev, background }));
  }, []);

  const setGroup = React.useCallback((group: number) => {
    setParticipant((prev) => ({ ...prev, group }));
  }, []);

  return (
    <ParticipantContext.Provider value={{ participant, setBackground, setGroup }}>
      {children}
    </ParticipantContext.Provider>
  );
};

export const useParticipant = () => {
  const context = useContext(ParticipantContext);
  if (context === undefined) {
    throw new Error('useParticipant must be used within a ParticipantProvider');
  }
  return context;
};
